import React from "react";
import EChart from "@src/reporting/components/EChart";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ClearIcon from "@mui/icons-material/Clear";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import { getLocale, rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./VotingCenterReport.module.styl";

const pad2 = (value) => String(value).padStart(2, "0");
const formatNumber = (value) => Number(value || 0).toLocaleString();

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const applicantUtilization = (row) => {
  const applicants = Number(row?.uniqueApplicantCount);
  const capacity = Number(row?.capacity);

  if (!Number.isFinite(applicants) || !Number.isFinite(capacity) || capacity <= 0) {
    return null;
  }

  return applicants * 100 / capacity;
};

const distinctApplications = (applications) => {
  const seen = new Set();
  const rows = [];

  for (const application of applications || []) {
    const ninu = String(application?.nationalIdentifier || "").trim().toUpperCase();
    const key = ninu || `gid:${application?.gid ?? rows.length}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(application);
  }

  return rows;
};

const datePart = (value) => String(value || "").slice(0, 10);

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  return String(value).replace("T", " ").slice(0, 19);
};

const formatTime = (value) => {
  if (!value) {
    return "—";
  }

  const text = String(value);
  const index = text.indexOf("T");
  return index >= 0 ? text.slice(index + 1, index + 9) : text.slice(-8);
};

const formatDateLabel = (dateString) => {
  if (!dateString) {
    return "—";
  }

  const date = new Date(`${dateString}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return new Intl.DateTimeFormat(getLocale(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(date);
};

const shiftDate = (dateString, delta) => {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + delta);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const fillDailyHistory = (days, firstSubmissionAt, lastSubmissionAt) => {
  const first = datePart(firstSubmissionAt);
  const last = datePart(lastSubmissionAt);

  if (!first || !last) {
    return [];
  }

  const byDate = new Map((days || []).map((row) => [row.date, row]));
  const cursor = new Date(`${first}T12:00:00`);
  const end = new Date(`${last}T12:00:00`);
  const out = [];

  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`;
    const row = byDate.get(key);

    out.push({
      date: key,
      count: Number(row?.count || 0),
      standardCount: Number(row?.standardCount || 0),
      correctionCount: Number(row?.correctionCount || 0),
      retransmissionCount: Number(row?.retransmissionCount || 0),
      duplicateCount: Number(row?.duplicateCount || 0),
      uniqueApplicantCount: Number(row?.uniqueApplicantCount || 0),
      operatorCount: Number(row?.operatorCount || 0)
    });

    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
};

const statusLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return t("common.submitted");
    case 2:
      return t("common.error");
    case 3:
      return t("common.approved");
    case 4:
      return t("voter.manualAdjudication");
    case 5:
      return t("voter.retransmission");
    case 6:
      return t("voter.duplicate");
    default:
      return value === null || value === undefined ? t("common.unknown") : String(value);
  }
};

const applicationTypeLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return t("voter.standard");
    case 2:
      return t("voter.correction");
    default:
      return value === null || value === undefined ? t("common.unknown") : String(value);
  }
};

const sourceLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return t("voter.oniSearch");
    case 2:
      return t("voter.mrzSlashNfc");
    case 3:
      return t("voter.manualEntry");
    default:
      return value === null || value === undefined ? t("common.unknown") : String(value);
  }
};

const operatorName = (row) => {
  const name = `${row?.firstName || row?.operatorFirstName || ""} ${row?.lastName || row?.operatorLastName || ""}`.trim();
  return name || row?.operatorUsername || row?.nationalId || row?.operatorNationalId || t("voter.operator");
};

const locationLabel = (report) =>
  [report?.sectionName, report?.communeName, report?.departmentName]
    .filter(Boolean)
    .join(" · ");

const Metric = ({ label, value, detail }) => (
  <div className={styles.metric}>
    <div className={styles.metricLabel}>{label}</div>
    <div className={styles.metricValue}>{value}</div>
    {detail ? <div className={styles.metricDetail}>{detail}</div> : null}
  </div>
);

const Panel = ({ eyebrow, title, aside, children, className = "" }) => (
  <section className={`${styles.panel} ${className}`.trim()}>
    <div className={styles.panelHeader}>
      <div>
        {eyebrow ? <div className={styles.panelEyebrow}>{eyebrow}</div> : null}
        <h2 className={styles.panelTitle}>{title}</h2>
      </div>
      {aside ? <div className={styles.panelAside}>{aside}</div> : null}
    </div>
    {children}
  </section>
);

const CenterHero = ({ report, gui }) => (
  <header className={styles.hero}>
    <div className={styles.heroTopRow}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => gui.goTo("/reporting/voting-centers")}
      >
        <ArrowBackIcon fontSize="small" />
        {t("reporting.votingCenterReports")}
      </button>
    </div>

    <div className={styles.heroBody}>
      <div className={styles.heroIconWrap}>
        <PlaceOutlinedIcon className={styles.heroIcon} />
      </div>

      <div className={styles.heroIdentity}>
        <div className={styles.eyebrow}>{t("reporting.votingCenterIntelligence")}</div>
        <h1 className={styles.title}>{report.name || t("voter.votingCenter")}</h1>
        <div className={styles.heroMetaRow}>
          <span className={styles.codeBadge}>{report.code || `#${report.gid}`}</span>
          {locationLabel(report) ? <span>{locationLabel(report)}</span> : null}
        </div>
        {report.address ? <div className={styles.address}>{report.address}</div> : null}
      </div>

      <div className={styles.heroSpan}>
        <div className={styles.spanLabel}>{t("reporting.registrationActivity")}</div>
        <div className={styles.spanValue}>
          {datePart(report.firstSubmissionAt) || "—"} → {datePart(report.lastSubmissionAt) || "—"}
        </div>
      </div>
    </div>
  </header>
);

const MapPanel = ({ report }) => {
  const lat = Number(report?.latitude);
  const lon = Number(report?.longitude);
  const valid = Number.isFinite(lat) && Number.isFinite(lon);

  if (!valid) {
    return (
      <Panel eyebrow={t("reporting.gis")} title={t("reporting.centerLocation")} className={styles.mapPanel}>
        <div className={styles.mapUnavailable}>
          <PublicOutlinedIcon />
          {t("reporting.noGpsCoordinatesForCenter")}
        </div>
      </Panel>
    );
  }

  const span = 0.025;
  const bbox = [lon - span, lat - span, lon + span, lat + span].join(",");
  const embedUrl =
    `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}`
    + `&layer=mapnik&marker=${encodeURIComponent(`${lat},${lon}`)}`;

  const openUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=15/${lat}/${lon}`;

  return (
    <Panel
      eyebrow={t("reporting.gis")}
      title={t("reporting.centerLocation")}
      className={styles.mapPanel}
      aside={(
        <button
          type="button"
          className={styles.externalButton}
          onClick={() => window.open(openUrl, "_blank", "noopener,noreferrer")}
        >
          {t("reporting.openMap")} <OpenInNewIcon fontSize="inherit" />
        </button>
      )}
    >
      <div className={styles.mapFrameWrap}>
        <iframe
          title={`${report.code || t("voter.votingCenter")} ${t("reporting.locationLower")}`}
          className={styles.mapFrame}
          src={embedUrl}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        <div className={styles.coordinateBadge}>{lat.toFixed(5)}, {lon.toFixed(5)}</div>
      </div>
    </Panel>
  );
};

const CapacityPanel = ({ report }) => {
  const capacity = Number(report?.capacity || 0);
  const applicants = Number(report?.uniqueApplicantCount || 0);
  const utilization = applicantUtilization(report);
  const remaining = capacity > 0 ? capacity - applicants : null;
  const visual = utilization !== null ? Math.max(0, Math.min(100, utilization)) : 0;

  return (
    <Panel eyebrow={t("reporting.applicantCapacity")} title={t("geo.capacity")} className={styles.capacityPanel}>
      <div className={styles.capacityMain}>
        <div className={styles.capacityNumber}>{formatNumber(applicants)}</div>
        <div className={styles.capacityCaption}>{t("reporting.applicantsLower")}</div>
      </div>

      <div className={styles.capacityTrack}>
        <span className={styles.capacityFill} style={{ width: `${visual}%` }} />
      </div>

      <div className={styles.capacityScale}>
        <span>0</span>
        <strong>{utilization !== null ? `${utilization.toFixed(1)}%` : "—"}</strong>
        <span>{capacity ? formatNumber(capacity) : "—"}</span>
      </div>

      <div className={styles.capacityFacts}>
        <div>
          <span>{remaining !== null && remaining < 0 ? t("reporting.overCapacity") : t("reporting.remaining")}</span>
          <strong>{remaining === null ? "—" : formatNumber(Math.abs(remaining))}</strong>
        </div>
        <div>
          <span>{t("reporting.plannedPollingStations")}</span>
          <strong>{report.capacityPollingStations ?? "—"}</strong>
        </div>
        <div>
          <span>{t("reporting.currentPollingStations")}</span>
          <strong>{report.lastPollingStation ?? "—"}</strong>
        </div>
      </div>
    </Panel>
  );
};

const TurnoutCapacityChart = ({ report }) => {
  const rows = Array.isArray(report.newApplicantDays) ? report.newApplicantDays : [];
  let cumulative = 0;
  const data = rows.map((row) => {
    cumulative += Number(row.count || 0);
    return [row.date, cumulative];
  });

  const capacity = Number(report.capacity || 0);

  const option = React.useMemo(() => ({
    animationDuration: 260,
    grid: { left: 56, right: 28, top: 28, bottom: 58 },
    tooltip: {
      trigger: "axis",
      formatter: (items) => {
        const item = items?.[0];
        return item ? `<strong>${item.axisValue}</strong><br/>${formatNumber(item.value?.[1])} ${t("reporting.cumulativeFirstTimeApplicants")}` : "";
      }
    },
    xAxis: {
      type: "category",
      data: data.map((row) => row[0]),
      axisTick: { show: false },
      axisLabel: { color: "#728097", formatter: (value) => String(value).slice(5), hideOverlap: true },
      axisLine: { lineStyle: { color: "#d8e0ec" } }
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { color: "#728097" },
      splitLine: { lineStyle: { color: "#eef2f7" } }
    },
    dataZoom: [
      { type: "inside", start: Math.max(0, 100 - Math.min(100, 50 / Math.max(1, data.length) * 100)), end: 100 },
      { type: "slider", height: 18, bottom: 12, borderColor: "#d8e0ec", backgroundColor: "#f4f7fb", fillerColor: "rgba(45,108,223,.14)" }
    ],
    series: [
      {
        type: "line",
        data: data.map((row) => row[1]),
        smooth: true,
        symbol: "none",
        lineStyle: { width: 3, color: "#245fce" },
        areaStyle: { color: "rgba(36,95,206,.10)" },
        markLine: {
          symbol: "none",
          label: { color: "#526079", formatter: (params) => params.name },
          lineStyle: { type: "dashed" },
          data: [
            ...(capacity > 0 ? [{ yAxis: capacity, name: `${t("geo.capacity")} ${formatNumber(capacity)}` }] : [])
          ]
        }
      }
    ]
  }), [JSON.stringify(data), capacity]);

  if (!data.length) {
    return <div className={styles.emptyChart}>{t("reporting.noApplicantHistory")}</div>;
  }

  return <EChart option={option} className={styles.chartTurnout} />;
};

const ActivityMatrix = ({ report, selectedDate, onSelectDate }) => {
  const history = React.useMemo(
    () => fillDailyHistory(report.days, report.firstSubmissionAt, report.lastSubmissionAt),
    [report.days, report.firstSubmissionAt, report.lastSubmissionAt]
  );

  const dates = React.useMemo(
    () => history.map((row) => row.date),
    [history]
  );

  const values = React.useMemo(
    () => (report.hourlyHistory || []).map((row) => ({
      value: [Number(row.hour), row.date, Number(row.count || 0)],
      itemStyle: row.date === selectedDate
        ? { borderColor: "#153e83", borderWidth: 2 }
        : { borderColor: "#ffffff", borderWidth: 1 }
    })),
    [report.hourlyHistory, selectedDate]
  );

  const max = Math.max(1, ...values.map((row) => Number(row.value[2] || 0)));

  const option = React.useMemo(() => ({
    animationDuration: 180,
    grid: { left: 92, right: 56, top: 32, bottom: 50 },
    tooltip: {
      formatter: (params) => {
        const value = params?.data?.value;
        if (!value) return "";
        return `<strong>${value[1]}</strong><br/>${pad2(value[0])}:00–${pad2(value[0])}:59<br/>${formatNumber(value[2])} ${t("reporting.applicantsLower")}`;
      }
    },
    xAxis: {
      type: "category",
      data: Array.from({ length: 24 }, (_, hour) => pad2(hour)),
      axisTick: { show: false },
      axisLabel: { color: "#718097", fontSize: 10 },
      axisLine: { lineStyle: { color: "#d8e0ec" } }
    },
    yAxis: {
      type: "category",
      data: dates,
      inverse: true,
      axisTick: { show: false },
      axisLabel: { color: "#718097", formatter: (value) => String(value).slice(5), fontSize: 10 },
      axisLine: { show: false }
    },
    visualMap: {
      min: 0,
      max,
      calculable: false,
      orient: "vertical",
      right: 2,
      top: 28,
      text: ["busy", "quiet"],
      textStyle: { color: "#7b8799", fontSize: 9 },
      inRange: { color: ["#f2f6fc", "#b8d0f3", "#5f8fdc", "#174ea6"] }
    },
    dataZoom: dates.length > 28 ? [
      { type: "inside", yAxisIndex: 0, start: Math.max(0, 100 - 28 / dates.length * 100), end: 100 },
      { type: "slider", yAxisIndex: 0, width: 14, right: 36, top: 34, bottom: 50 }
    ] : [],
    series: [{
      type: "heatmap",
      data: values,
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: "rgba(15,35,70,.22)" } }
    }]
  }), [dates.join("|"), JSON.stringify(values), max]);

  if (!dates.length) {
    return <div className={styles.emptyChart}>{t("reporting.noActivityVisualize")}</div>;
  }

  return (
    <EChart
      option={option}
      className={styles.chartMatrix}
      onClick={(params) => {
        const date = params?.data?.value?.[1];
        if (date) onSelectDate(date);
      }}
    />
  );
};

const DailyHistory = ({ report, selectedDate, onSelectDate }) => {
  const rows = React.useMemo(
    () => fillDailyHistory(report.days, report.firstSubmissionAt, report.lastSubmissionAt),
    [report.days, report.firstSubmissionAt, report.lastSubmissionAt]
  );

  const option = React.useMemo(() => ({
    animationDuration: 240,
    grid: { left: 50, right: 50, top: 30, bottom: 66 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    xAxis: {
      type: "category",
      data: rows.map((row) => row.date),
      axisTick: { show: false },
      axisLabel: { color: "#728097", formatter: (value) => String(value).slice(5), hideOverlap: true },
      axisLine: { lineStyle: { color: "#d8e0ec" } }
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { color: "#728097" },
      splitLine: { lineStyle: { color: "#eef2f7" } }
    },
    dataZoom: [
      { type: "inside", start: Math.max(0, 100 - Math.min(100, 45 / Math.max(1, rows.length) * 100)), end: 100 },
      { type: "slider", height: 18, bottom: 12, borderColor: "#d8e0ec", backgroundColor: "#f4f7fb", fillerColor: "rgba(45,108,223,.14)" }
    ],
    series: [
      {
        name: t("reporting.applicants"),
        type: "bar",
        barMaxWidth: 18,
        data: rows.map((row) => ({
          value: Number(row.uniqueApplicantCount || 0),
          itemStyle: { color: row.date === selectedDate ? "#174ea6" : "#7ea4e6", borderRadius: [4, 4, 0, 0] }
        }))
      }
    ]
  }), [JSON.stringify(rows), selectedDate]);

  if (!rows.length) {
    return <div className={styles.emptyChart}>{t("reporting.noApplicantHistory")}</div>;
  }

  return (
    <EChart
      option={option}
      className={styles.chartHistory}
      onClick={(params) => {
        const date = params?.name;
        if (date) onSelectDate(date);
      }}
    />
  );
};

const HourlyHistogram = ({ hours, selectedHour, onSelectHour }) => {
  const normalized = Array.from({ length: 24 }, (_, hour) => {
    const row = (hours || []).find((item) => Number(item.hour) === hour);
    return Number(row?.count || 0);
  });

  const option = React.useMemo(() => ({
    animationDuration: 200,
    grid: { left: 44, right: 16, top: 24, bottom: 38 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (items) => {
        const item = items?.[0];
        return item ? `<strong>${item.axisValue}</strong><br/>${formatNumber(item.value)} ${t("reporting.applicantsLower")}` : "";
      }
    },
    xAxis: {
      type: "category",
      data: Array.from({ length: 24 }, (_, hour) => `${pad2(hour)}:00`),
      axisTick: { show: false },
      axisLabel: { color: "#738097", interval: 1, fontSize: 10 },
      axisLine: { lineStyle: { color: "#d8e0ec" } }
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { color: "#738097" },
      splitLine: { lineStyle: { color: "#eef2f7" } }
    },
    series: [{
      type: "bar",
      barMaxWidth: 24,
      data: normalized.map((count, hour) => ({
        value: count,
        itemStyle: { color: selectedHour === hour ? "#153e83" : "#5f8fdc", borderRadius: [5, 5, 0, 0] }
      }))
    }]
  }), [normalized.join(","), selectedHour]);

  return (
    <EChart
      option={option}
      className={styles.chartDay}
      onClick={(params) => {
        const hour = Number(String(params?.name || "").slice(0, 2));
        if (Number.isFinite(hour)) {
          onSelectHour(selectedHour === hour ? null : hour);
        }
      }}
    />
  );
};

const CadenceChart = ({ applications, gui }) => {
  const option = React.useMemo(() => {
    const points = (applications || []).map((app, index) => ({
      value: [app.submittedAt, index % 7],
      gid: app.gid,
      application: app
    }));

    return {
      animationDuration: 160,
      grid: { left: 18, right: 18, top: 18, bottom: 36 },
      tooltip: {
        formatter: (params) => {
          const app = params?.data?.application;
          if (!app) return "";
          return [
            `<strong>${formatTime(app.submittedAt)}</strong>`,
            `NINU ${app.nationalIdentifier || "—"}`,
            `${t("voter.operator")} ${operatorName(app)}`,
            app.receiptNumber ? `${t("voter.receipt")} ${app.receiptNumber}` : null
          ].filter(Boolean).join("<br/>");
        }
      },
      xAxis: {
        type: "time",
        axisLabel: { color: "#738097" },
        splitLine: { show: true, lineStyle: { color: "#f0f3f8" } },
        axisLine: { lineStyle: { color: "#d8e0ec" } }
      },
      yAxis: { type: "value", min: -1, max: 7, show: false },
      series: [{
        type: "scatter",
        data: points,
        symbolSize: 9,
        itemStyle: { color: "#245fce", opacity: 0.75 },
        emphasis: { scale: 1.8 }
      }]
    };
  }, [applications]);

  if (!applications?.length) {
    return <div className={styles.emptyChart}>{t("reporting.noApplicantsDay")}</div>;
  }

  return (
    <EChart
      option={option}
      className={styles.chartCadence}
      onClick={can(gui, "READ_VOTER_APPLICATION") ? (params) => {
        const gid = params?.data?.gid;
        if (gid) gui.goTo(`/voter-application/${gid}`);
      } : undefined}
    />
  );
};

const DonutChart = ({ rows, labelFor, codeField }) => {
  const data = (rows || []).map((row) => ({
    name: labelFor(row?.[codeField]),
    value: Number(row.count || 0)
  }));

  const option = React.useMemo(() => ({
    animationDuration: 220,
    tooltip: { trigger: "item", formatter: "{b}<br/>{c} ({d}%)" },
    legend: { bottom: 0, textStyle: { color: "#637088" } },
    series: [{
      type: "pie",
      radius: ["47%", "72%"],
      center: ["50%", "43%"],
      avoidLabelOverlap: true,
      label: { show: false },
      emphasis: { label: { show: true, fontWeight: "bold" } },
      data
    }]
  }), [JSON.stringify(data)]);

  return <EChart option={option} className={styles.chartCompact} />;
};

const SourceChart = ({ rows }) => {
  const data = (rows || []).map((row) => ({
    label: sourceLabel(row.source),
    count: Number(row.count || 0)
  }));

  const option = React.useMemo(() => ({
    animationDuration: 220,
    grid: { left: 112, right: 24, top: 16, bottom: 26 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#738097" }, splitLine: { lineStyle: { color: "#eef2f7" } } },
    yAxis: {
      type: "category",
      inverse: true,
      data: data.map((row) => row.label),
      axisTick: { show: false },
      axisLabel: { color: "#526079" },
      axisLine: { show: false }
    },
    series: [{
      type: "bar",
      data: data.map((row) => row.count),
      barMaxWidth: 22,
      itemStyle: { color: "#5f8fdc", borderRadius: [0, 5, 5, 0] }
    }]
  }), [JSON.stringify(data)]);

  return <EChart option={option} className={styles.chartCompact} />;
};

const OperatorsPanel = ({ report, gui }) => {
  const rows = Array.isArray(report.operators) ? report.operators : [];
  const top = rows.slice(0, 12);

  const option = React.useMemo(() => ({
    animationDuration: 220,
    grid: { left: 145, right: 26, top: 14, bottom: 28 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#738097" }, splitLine: { lineStyle: { color: "#eef2f7" } } },
    yAxis: {
      type: "category",
      inverse: true,
      data: top.map((row) => operatorName(row)),
      axisTick: { show: false },
      axisLabel: { color: "#526079", width: 132, overflow: "truncate" },
      axisLine: { show: false }
    },
    series: [{
      type: "bar",
      data: top.map((row) => ({
        value: Number(row.applicationCount || 0),
        itemStyle: {
          color: Number(row.assignedElsewhereApplicationCount || 0) > 0 ? "#b97542" : "#5f8fdc",
          borderRadius: [0, 5, 5, 0]
        }
      })),
      barMaxWidth: 18
    }]
  }), [JSON.stringify(top)]);

  const crossCenter = rows.reduce(
    (sum, row) => sum + Number(row.assignedElsewhereApplicationCount || 0),
    0
  );

  return (
    <Panel
      eyebrow={t("reporting.workforce")}
      title={t("reporting.operatorComposition")}
      aside={<span className={styles.countBadge}>{rows.length} {t("reporting.ninuIdentities")}</span>}
    >
      <div className={styles.operatorSummary}>
        <div><GroupsOutlinedIcon fontSize="small" /><strong>{formatNumber(rows.length)}</strong><span>{t("reporting.operatorsActiveHere")}</span></div>
        <div><OpenInNewIcon fontSize="small" /><strong>{formatNumber(crossCenter)}</strong><span>{t("reporting.applicantsElsewhere")}</span></div>
      </div>

      <EChart option={option} className={styles.chartOperators} />

      <div className={styles.operatorTableShell}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("voter.operator")}</th>
              <th>{t("voter.ninu")}</th>
              <th>{t("reporting.applicants")}</th>
              <th>{t("reporting.assignedElsewhere")}</th>
              <th>{t("user.accounts")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.nationalId}>
                <td><strong>{operatorName(row)}</strong></td>
                <td><span className={styles.ninu}>{row.nationalId}</span></td>
                <td>{formatNumber(row.applicationCount)}</td>
                <td>
                  {Number(row.assignedElsewhereApplicationCount || 0) > 0 ? (
                    <span className={styles.attentionTag}>{formatNumber(row.assignedElsewhereApplicationCount)}</span>
                  ) : "—"}
                </td>
                <td>
                  <div className={styles.accountMiniList}>
                    {(row.accounts || []).map((account) => {
                      const label = (
                        <>
                          {account.username || `#${account.gid}`}
                          {account.votingCenterCode ? ` · ${account.votingCenterCode}` : ""}
                        </>
                      );

                      return can(gui, "READ_USER_ACCOUNT") && account.gid ? (
                        <button
                          key={account.gid || account.username}
                          type="button"
                          className={styles.accountMini}
                          onClick={() => gui.goTo(`/user-account/${account.gid}`)}
                        >
                          {label}
                        </button>
                      ) : (
                        <span
                          key={account.gid || account.username}
                          className={styles.accountMini}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className={styles.actionCell}>
                  <Tooltip title={t("reporting.operatorReport")}>
                    <IconButton
                      size="small"
                      onClick={() => row.nationalId && gui.goTo(`/reporting/operators/${encodeURIComponent(row.nationalId)}`)}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const CatchmentPanel = ({ rows }) => {
  const data = (rows || []).slice(0, 18);
  const option = React.useMemo(() => ({
    animationDuration: 220,
    grid: { left: 180, right: 28, top: 18, bottom: 30 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (items) => {
        const item = items?.[0];
        const row = data[item?.dataIndex];
        return row
          ? `<strong>${row.sectionName || t("reporting.unknownResidenceSection")}</strong><br/>${row.communeName || ""}<br/>${formatNumber(row.uniqueApplicantCount)} ${t("reporting.applicantsLower")}`
          : "";
      }
    },
    xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#738097" }, splitLine: { lineStyle: { color: "#eef2f7" } } },
    yAxis: {
      type: "category",
      inverse: true,
      data: data.map((row) => row.sectionName || t("reporting.unknownSection")),
      axisTick: { show: false },
      axisLabel: { color: "#526079", width: 165, overflow: "truncate" },
      axisLine: { show: false }
    },
    series: [{
      type: "bar",
      data: data.map((row) => row.uniqueApplicantCount),
      barMaxWidth: 18,
      itemStyle: { color: "#648fd4", borderRadius: [0, 5, 5, 0] }
    }]
  }), [JSON.stringify(data)]);

  return (
    <Panel eyebrow={t("reporting.residenceGeography")} title={t("reporting.applicantCatchment")}>
      <EChart option={option} className={styles.chartCatchment} />
    </Panel>
  );
};

const NearbyCapacityField = ({ report, gui }) => {
  const lat = Number(report.latitude);
  const lon = Number(report.longitude);
  const nearby = (report.nearbyCenters || []).filter(
    (row) => Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude))
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  const utilizationValues = nearby
    .map((row) => numberOrNull(row.capacityUtilization))
    .filter((value) => value !== null);
  const selectedUtilization = numberOrNull(report.capacityUtilization) || 0;
  const maxUtil = Math.max(100, ...utilizationValues, selectedUtilization);

  const option = React.useMemo(() => ({
    animationDuration: 260,
    grid: { left: 52, right: 62, top: 28, bottom: 46 },
    tooltip: {
      formatter: (params) => {
        const row = params?.data?.center;
        if (!row) return "";
        return [
          `<strong>${row.code || ""} ${row.name || ""}</strong>`,
          row.distanceKm !== undefined ? `${Number(row.distanceKm).toFixed(1)} ${t("reporting.kmAway")}` : t("reporting.selectedCenter"),
          `${formatNumber(row.uniqueApplicantCount)} / ${formatNumber(row.capacity)} ${t("reporting.applicantsLower")}`,
          numberOrNull(row.capacityUtilization) !== null ? `${numberOrNull(row.capacityUtilization).toFixed(1)}% ${t("reporting.utilized")}` : null
        ].filter(Boolean).join("<br/>");
      }
    },
    xAxis: {
      type: "value",
      name: "longitude",
      nameLocation: "middle",
      nameGap: 28,
      axisLabel: { color: "#7a879a", formatter: (value) => Number(value).toFixed(2) },
      splitLine: { lineStyle: { color: "#edf1f6" } }
    },
    yAxis: {
      type: "value",
      name: "latitude",
      nameLocation: "middle",
      nameGap: 36,
      axisLabel: { color: "#7a879a", formatter: (value) => Number(value).toFixed(2) },
      splitLine: { lineStyle: { color: "#edf1f6" } }
    },
    visualMap: {
      min: 0,
      max: maxUtil,
      dimension: 2,
      orient: "vertical",
      right: 0,
      top: 34,
      text: ["utilization", ""],
      textStyle: { color: "#7a879a", fontSize: 9 },
      inRange: { color: ["#b8d0f3", "#5f8fdc", "#c06f36"] }
    },
    series: [
      {
        type: "scatter",
        data: nearby.map((row) => ({
          value: [Number(row.longitude), Number(row.latitude), numberOrNull(row.capacityUtilization) || 0, Number(row.capacity || 0)],
          gid: row.gid,
          center: row
        })),
        symbolSize: (value) => Math.max(9, Math.min(34, 8 + Math.sqrt(Math.max(0, Number(value?.[3] || 0))) / 4)),
        itemStyle: { opacity: 0.78 },
        label: {
          show: true,
          position: "top",
          formatter: (params) => formatNumber(params?.data?.center?.uniqueApplicantCount)
        },
        emphasis: { scale: 1.35 }
      },
      {
        type: "scatter",
        data: [{
          value: [lon, lat, selectedUtilization, Number(report.capacity || 0)],
          center: report,
          gid: report.gid
        }],
        symbol: "diamond",
        symbolSize: 28,
        itemStyle: { color: "#153e83", borderColor: "#ffffff", borderWidth: 3 },
        label: {
          show: true,
          position: "top",
          formatter: `${report.code || t("reporting.selected")} · ${formatNumber(report.uniqueApplicantCount)}`,
          color: "#153e83",
          fontWeight: "bold"
        }
      }
    ]
  }), [JSON.stringify(nearby), lat, lon, maxUtil]);

  return (
    <Panel
      eyebrow={t("reporting.nearbyCapacity")}
      title={t("reporting.gpsCapacityField")}
      aside={<span className={styles.countBadge}>{nearby.length} {t("reporting.centersWithin75Km")}</span>}
    >
      <div className={styles.gisLegend}>
        {t("reporting.nearbyCapacityLegend")}
      </div>
      <EChart
        option={option}
        className={styles.chartNearby}
        onClick={(params) => {
          const gid = params?.data?.gid;
          if (gid && Number(gid) !== Number(report.gid)) {
            gui.goTo(`/reporting/voting-centers/${gid}`);
          }
        }}
      />
    </Panel>
  );
};

const DayApplications = ({ day, centerGid, selectedHour, gui, onClearHour }) => {
  const rows = React.useMemo(() => {
    const all = distinctApplications(Array.isArray(day?.applications) ? day.applications : []);
    if (selectedHour === null || selectedHour === undefined) return all;

    return all.filter((row) => Number(String(row.submittedAt || "").slice(11, 13)) === selectedHour);
  }, [day, selectedHour]);

  return (
    <Panel
      eyebrow={t("reporting.drillDown")}
      title={`${t("reporting.applicants")}${selectedHour !== null && selectedHour !== undefined ? ` · ${pad2(selectedHour)}:00–${pad2(selectedHour)}:59` : ""}`}
      aside={(
        <div className={styles.tableHeaderRight}>
          <span className={styles.countBadge}>{rows.length}</span>
          {selectedHour !== null && selectedHour !== undefined ? (
            <Tooltip title={t("reporting.clearHourFilter")}>
              <IconButton size="small" onClick={onClearHour}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      )}
    >
      <div className={styles.tableShell}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("common.time")}</th>
              <th>{t("voter.applicantNinu")}</th>
              <th>{t("voter.operator")}</th>
              <th>{t("common.type")}</th>
              <th>{t("common.source")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => {
              const assignedElsewhere = row.operatorAssignedCenterGid
                && Number(row.operatorAssignedCenterGid) !== Number(centerGid);

              return (
                <tr key={`${row.gid}-${row.submittedAt}`}>
                  <td className={styles.timeCell}>{formatTime(row.submittedAt)}</td>
                  <td><span className={styles.ninu}>{row.nationalIdentifier || "—"}</span></td>
                  <td>
                    {row.operatorNationalId ? (
                      <button
                        type="button"
                        className={styles.inlineLink}
                        onClick={() => gui.goTo(`/reporting/operators/${encodeURIComponent(row.operatorNationalId)}`)}
                      >
                        {operatorName(row)}
                      </button>
                    ) : operatorName(row)}
                    {assignedElsewhere ? (
                      <div className={styles.assignedElsewhere}>
                        {t("reporting.assigned")} {row.operatorAssignedCenterCode || row.operatorAssignedCenterName || row.operatorAssignedCenterGid}
                      </div>
                    ) : null}
                  </td>
                  <td>{applicationTypeLabel(row.applicationType)}</td>
                  <td>{sourceLabel(row.source)}</td>
                  <td className={styles.actionCell}>
                    {can(gui, "READ_VOTER_APPLICATION") ? (
                      <Tooltip title={t("voter.viewApplication")}>
                        <IconButton size="small" onClick={() => row.gid && gui.goTo(`/voter-application/${row.gid}`)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={6} className={styles.emptyTable}>{t("reporting.noApplicantsSelection")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const VotingCenterReport = ({ gui, gid }) => {
  const [report, setReport] = React.useState(null);
  const [day, setDay] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState("");
  const [selectedHour, setSelectedHour] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [dayLoading, setDayLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await rest.request(
          getServiceUri() + `reporting/voting-center-reports/${Number(gid)}`,
          "GET"
        );

        if (cancelled) return;

        setReport(response || null);
        setSelectedDate(datePart(response?.lastSubmissionAt) || datePart(response?.firstSubmissionAt) || "");
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setReport(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [gid]);

  React.useEffect(() => {
    if (!selectedDate) {
      setDay(null);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setDayLoading(true);
      setSelectedHour(null);

      try {
        const response = await rest.request(
          getServiceUri()
            + `reporting/voting-center-reports/${Number(gid)}`
            + `/day/${encodeURIComponent(selectedDate)}`,
          "GET"
        );

        if (!cancelled) setDay(response || null);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setDay(null);
        }
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [gid, selectedDate]);

  if (loading) {
    return <div className={styles.loading}>{t("reporting.loadingCenter")}</div>;
  }

  if (error && !report) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!report) {
    return <div className={styles.error}>{t("reporting.centerNotFound")}</div>;
  }

  const busiestDay = (report.days || []).reduce(
    (best, row) => Number(row.uniqueApplicantCount || 0) > Number(best?.uniqueApplicantCount || 0) ? row : best,
    null
  );

  const firstActivityDate = datePart(report.firstSubmissionAt);
  const lastActivityDate = datePart(report.lastSubmissionAt);
  const canGoPrevious = Boolean(selectedDate && firstActivityDate && selectedDate > firstActivityDate);
  const canGoNext = Boolean(selectedDate && lastActivityDate && selectedDate < lastActivityDate);

  return (
    <div className={styles.page}>
      <CenterHero report={report} gui={gui} />

      {error ? <div className={styles.errorInline}>{error}</div> : null}

      <div className={styles.metrics}>
        <Metric label={t("reporting.applicants")} value={formatNumber(report.uniqueApplicantCount)} detail={report.capacity ? `${t("reporting.ofCapacity")} ${formatNumber(report.capacity)} ${t("geo.capacity").toLowerCase()}` : null} />
        <Metric label={t("reporting.capacityUtilization")} value={applicantUtilization(report) !== null ? `${applicantUtilization(report).toFixed(1)}%` : "—"} />
        <Metric label={t("reporting.operators")} value={formatNumber(report.activeOperatorCount)} />
        <Metric label={t("reporting.activeDays")} value={formatNumber(report.activeDayCount)} detail={busiestDay ? `${t("reporting.peak")} ${formatNumber(busiestDay.uniqueApplicantCount)} ${t("reporting.on")} ${busiestDay.date}` : null} />
      </div>

      <div className={styles.gridTwoTop}>
        <MapPanel report={report} />
        <CapacityPanel report={report} />
      </div>

      <Panel eyebrow={t("reporting.turnoutTrajectory")} title={t("reporting.firstApplicantsCapacity")}>
        <TurnoutCapacityChart report={report} />
      </Panel>

      <Panel
        eyebrow={t("reporting.temporalFingerprint")}
        title={t("reporting.dateHourMatrix")}
        aside={<span className={styles.hint}>{t("reporting.clickCellInspect")}</span>}
      >
        <ActivityMatrix report={report} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      </Panel>

      <Panel eyebrow={t("reporting.history")} title={t("reporting.dailyApplicantActivity")}>
        <DailyHistory report={report} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      </Panel>

      <Panel
        eyebrow={t("reporting.dailyForensicView")}
        title={selectedDate ? formatDateLabel(selectedDate) : t("reporting.selectDay")}
        aside={(
          <div className={styles.dayNav}>
            <Tooltip title={t("reporting.previousDay")}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canGoPrevious}
                  onClick={() => canGoPrevious && setSelectedDate(shiftDate(selectedDate, -1))}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            <span className={styles.dayCount}>{dayLoading ? t("reporting.loadingEllipsis") : `${formatNumber(day?.uniqueApplicantCount || 0)} ${t("reporting.applicantsLower")}`}</span>
            <Tooltip title={t("reporting.nextDay")}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canGoNext}
                  onClick={() => canGoNext && setSelectedDate(shiftDate(selectedDate, 1))}
                >
                  <ChevronRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      >
        {day ? (
          <>
            <div className={styles.dayFacts}>
              <span><strong>{formatNumber(day.uniqueApplicantCount)}</strong> {t("reporting.applicantsLower")}</span>
              <span><strong>{formatNumber(day.operatorCount)}</strong> {t("reporting.operatorsLower")}</span>
              <span>{t("reporting.firstLower")} <strong>{formatTime(day.firstSubmissionAt)}</strong></span>
              <span>{t("reporting.lastLower")} <strong>{formatTime(day.lastSubmissionAt)}</strong></span>
            </div>

            <HourlyHistogram hours={day.hours} selectedHour={selectedHour} onSelectHour={setSelectedHour} />

            <div className={styles.cadenceTitle}>
              <SpeedOutlinedIcon fontSize="small" /> {t("reporting.applicantCadence")}
            </div>
            <CadenceChart applications={distinctApplications(day.applications)} gui={gui} />
          </>
        ) : (
          <div className={styles.emptyChart}>{t("reporting.noDaySelected")}</div>
        )}
      </Panel>

      <OperatorsPanel report={report} gui={gui} />

      <div className={styles.gridTwoWide}>
        <CatchmentPanel rows={report.catchment} />
        <NearbyCapacityField report={report} gui={gui} />
      </div>

      {day ? (
        <DayApplications
          day={day}
          centerGid={report.gid}
          selectedHour={selectedHour}
          gui={gui}
          onClearHour={() => setSelectedHour(null)}
        />
      ) : null}
    </div>
  );
};

export const displayVotingCenterReport = (gui, gid) => ({
  uuid: `voting-center-report-${gid}`,
  view: () => <VotingCenterReport gui={gui} gid={gid} />
});

export default displayVotingCenterReport;
